"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Menu, X, ChevronRight, Phone, Mail, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerClose } from "@/components/ui/drawer";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";

// Recursive dropdown items renderer for desktop navigation
function DesktopSubmenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="min-w-[220px] p-2 space-y-1 bg-[hsl(40_30%_97%)] text-primary border border-ring/30 rounded-xs shadow-xl">
      {items.map((subItem, idx) => <li key={subItem.id || `sub-${idx}`} data-index={idx} className="relative group/sub">
          {subItem.children && subItem.children.length > 0 ? <div className="flex flex-col">
              <Link to={subItem.href || "#"} className="flex items-center justify-between px-3 py-2 text-xs font-serif uppercase tracking-wider text-primary hover:text-ring hover:bg-black/5 rounded-xs transition-colors">
                <span>{subItem.label}</span>
                <ChevronRight className="size-3 text-ring/70" />
              </Link>
              <div className="pl-3 border-l border-ring/20 ml-3 my-1">
                <DesktopSubmenu items={subItem.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={subItem.href || "#"} className="block px-3 py-2 text-xs font-serif uppercase tracking-wider text-primary hover:text-ring hover:bg-black/5 rounded-xs transition-colors">
                {subItem.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Leaf consumer for desktop navigation menu
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-4">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-black/10 animate-pulse rounded-xs" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-1 xl:gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id || `menu-${i}`} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-primary hover:bg-black/5 hover:text-ring focus:bg-black/5 focus:text-ring data-[state=open]:bg-black/5 data-[state=open]:text-ring font-serif text-xs uppercase tracking-[0.08em] font-semibold h-9 px-3 rounded-xs transition-colors border-0">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="p-0 border-0 bg-transparent shadow-none">
                  <DesktopSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex items-center justify-center font-serif text-xs uppercase tracking-[0.08em] font-semibold text-primary hover:text-ring px-3 py-2 rounded-xs transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive list for mobile drawer navigation
function MobileSubmenu({
  items,
  depth = 1
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={`space-y-1 ${depth > 0 ? "pl-3 border-l border-ring/25 mt-1" : ""}`}>
      {items.map((item, idx) => <li key={item.id || `mob-sub-${idx}`} data-index={idx} className="py-1">
          <Link to={item.href || "#"} className="block text-xs font-serif uppercase tracking-wider text-primary hover:text-ring transition-colors py-1">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <MobileSubmenu items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}

// Leaf consumer for mobile drawer navigation
function MobileNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 py-4">
        {[1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="h-6 w-3/4 bg-black/10 animate-pulse rounded-xs" />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-3 py-2">
      {menuItems.map((item, i) => <div key={item.id || `mob-item-${i}`} data-index={i} className="border-b border-black/10 pb-2.5">
          <Link to={item.href || "#"} className="block font-serif text-sm font-semibold uppercase tracking-[0.08em] text-primary hover:text-ring transition-colors">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="mt-1.5">
              <MobileSubmenu items={item.children} />
            </div>}
        </div>)}
    </nav>;
}
export default function Header() {
  const shouldReduceMotion = useReducedMotion();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Smooth scroll listener for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 76;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className="sticky top-0 z-50 w-full bg-[hsl(40_30%_97%)] text-primary shadow-md transition-colors">
      {/* Top micro-bar for institutional credentials */}
      <div className="hidden md:block border-b border-black/10 bg-[hsl(40_30%_97%)]/90 text-[11px] font-sans text-primary/80">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-8 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <Clock className="size-3 text-ring" />
              <span>Lunes a Viernes: 9:00 - 18:00</span>
            </span>
            <span className="hidden lg:inline-flex items-center gap-1.5">
              <span className="text-ring font-bold font-serif">·</span>
              <span>Estrategia · Cumplimiento · Resultados</span>
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a href="tel:6672505982" className="flex items-center gap-1.5 hover:text-ring transition-colors">
              <Phone className="size-3 text-ring" />
              <span>667 250 5982</span>
            </a>
            <span className="text-black/20">|</span>
            <a href="mailto:javilez@avsconsultores.org" className="flex items-center gap-1.5 hover:text-ring transition-colors">
              <Mail className="size-3 text-ring" />
              <span>javilez@avsconsultores.org</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation masthead */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-18 lg:h-20 flex items-center justify-between gap-4">
        {/* Monogram Brand Identity */}
        <div className="flex items-center shrink-0">
          <Link to="/" className="inline-flex items-center py-1">
            <WvcLogo className="h-12 lg:h-14 w-auto" />
          </Link>
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="2">
            <DesktopNavConsumer />
          </MenuProvider>
        </div>

        {/* Primary CTA and Mobile Menu Trigger */}
        <div className="flex items-center gap-3 shrink-0">
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: "easeOut"
        }} className="hidden sm:block">
            <Button asChild className="bg-[hsl(40_30%_97%)] text-primary border border-ring font-serif text-xs uppercase tracking-[0.08em] font-semibold px-5 lg:px-6 py-2 h-10 rounded-xs transition-[background-color,color,border-color] duration-250 hover:bg-ring hover:text-primary">
              <Link to="/ContactPage">
                Solicitar asesoría
              </Link>
            </Button>
          </motion.div>

          {/* Accessible Drawer for Mobile Devices */}
          <div className="lg:hidden">
            <SafeHydrate><Drawer open={drawerOpen} onOpenChange={setDrawerOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="text-primary hover:text-ring hover:bg-black/5 rounded-xs size-10" aria-label="Abrir menú de navegación">
                  <Menu className="size-6 text-primary" />
                </Button>
              </DrawerTrigger>

              <DrawerContent className="bg-[hsl(40_30%_97%)] text-primary border-l border-ring/40 p-6 flex flex-col justify-between max-w-sm w-full h-full">
                <div>
                  <DrawerHeader className="p-0 mb-6 flex items-center justify-between text-left">
                    <DrawerTitle className="text-primary font-serif uppercase text-sm tracking-[0.1em] font-semibold">
                      Menú institucional
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon" className="text-primary hover:text-ring hover:bg-black/5 rounded-xs size-8" aria-label="Cerrar menú">
                        <X className="size-5" />
                      </Button>
                    </DrawerClose>
                  </DrawerHeader>

                  <div className="h-[1.5px] w-12 bg-ring mb-6" />

                  <div className="overflow-y-auto max-h-[calc(100vh-260px)] pr-2">
                    <MenuProvider menu_id="2">
                      <MobileNavConsumer />
                    </MenuProvider>
                  </div>
                </div>

                <div className="pt-6 border-t border-black/10 space-y-4">
                  <Button asChild onClick={() => setDrawerOpen(false)} className="w-full bg-[hsl(40_30%_97%)] text-primary border border-ring font-serif text-xs uppercase tracking-[0.08em] font-semibold py-3 h-11 rounded-xs transition-colors hover:bg-ring hover:text-primary">
                    <Link to="/ContactPage">
                      Solicitar asesoría
                    </Link>
                  </Button>

                  <div className="space-y-2 text-xs text-primary/80 font-sans pt-2">
                    <a href="tel:6672505982" className="flex items-center gap-2 hover:text-ring transition-colors">
                      <Phone className="size-3.5 text-ring" />
                      <span>667 250 5982</span>
                    </a>
                    <a href="mailto:javilez@avsconsultores.org" className="flex items-center gap-2 hover:text-ring transition-colors">
                      <Mail className="size-3.5 text-ring" />
                      <span>javilez@avsconsultores.org</span>
                    </a>
                  </div>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </div>

      {/* Signature 1.5px ASV Satin Gold Hairline Partition */}
      <div className="w-full h-[1.5px] bg-ring/60" />
    </section>;
}